import { supabase } from '../../shared/supabase/supabaseClient';
import {
  CASH_SUMMARY_REPORTS_CACHE_MS,
  REPORTS_REMOTE_CACHE_MS,
  reportsCacheKey,
  withReportsCache,
} from './reportsRemoteCache';
import {
  buildCashSessionPaymentBreakdown,
  type CashSessionRow,
  type CashSummary,
} from '../../cash/domain-api';
import type { BranchCashSessionReportRow } from '../domain/reportsTypes';
import { mapRpcError, parseBranchCashSessionReportRow, parseCashSessionRow } from './reportsRemoteShared';

/** Aperturas de caja en rango (mismo criterio que ventas del período). */
export async function fetchRemoteBranchCashSessions(
  branchId: string,
  fromIso: string,
  toIso: string
): Promise<BranchCashSessionReportRow[] | null> {
  return withReportsCache(
    reportsCacheKey(['reports_branch_cash_sessions', branchId, fromIso, toIso]),
    async () => {
      const { data, error } = await supabase.rpc('reports_branch_cash_sessions', {
        p_branch_id: branchId,
        p_from: fromIso,
        p_to: toIso,
      });
      if (error) {
        console.warn('[reportsRemote] reports_branch_cash_sessions', mapRpcError(error));
        return null;
      }
      if (!Array.isArray(data)) return null;
      const out: BranchCashSessionReportRow[] = [];
      for (const row of data as Record<string, unknown>[]) {
        const parsed = parseBranchCashSessionReportRow(row);
        if (parsed) out.push(parsed);
      }
      return out;
    },
    REPORTS_REMOTE_CACHE_MS,
    { category: 'reports', name: 'reports_branch_cash_sessions' }
  );
}

/** Cierres de caja en rango (closed_at), paginado — alineado con SQLite local en Caja y turnos. */
export type RemoteCashClosuresQuery = {
  fromIso: string;
  toIso: string;
  limit: number;
  offset: number;
  openedBy?: string | null;
  differenceFilter?: string;
  sort?: string;
};

export async function fetchRemoteCashClosuresHistory(
  branchId: string,
  opts: RemoteCashClosuresQuery
): Promise<CashSessionRow[] | null> {
  return withReportsCache(
    reportsCacheKey([
      'reports_branch_cash_closures',
      branchId,
      opts.fromIso,
      opts.toIso,
      opts.limit,
      opts.offset,
      opts.openedBy,
      opts.differenceFilter,
      opts.sort,
    ]),
    async () => {
      const { data, error } = await supabase.rpc('reports_branch_cash_closures', {
        p_branch_id: branchId,
        p_from: opts.fromIso,
        p_to: opts.toIso,
        p_limit: opts.limit,
        p_offset: opts.offset,
        p_opened_by: opts.openedBy ?? null,
        p_difference_filter: opts.differenceFilter ?? 'all',
        p_sort: opts.sort ?? 'closed_desc',
      });
      if (error) {
        console.warn('[reportsRemote] reports_branch_cash_closures', mapRpcError(error));
        return null;
      }
      if (!Array.isArray(data)) return null;
      const out: CashSessionRow[] = [];
      for (const row of data as Record<string, unknown>[]) {
        const parsed = parseCashSessionRow(row);
        if (parsed) out.push(parsed);
      }
      return out;
    },
    REPORTS_REMOTE_CACHE_MS,
    { category: 'reports', name: 'reports_branch_cash_closures' }
  );
}

export async function fetchRemoteCashClosuresCount(
  branchId: string,
  opts: {
    fromIso: string;
    toIso: string;
    openedBy?: string | null;
    differenceFilter?: string;
  }
): Promise<number | null> {
  return withReportsCache(
    reportsCacheKey([
      'reports_branch_cash_closures_count',
      branchId,
      opts.fromIso,
      opts.toIso,
      opts.openedBy,
      opts.differenceFilter,
    ]),
    async () => {
      const { data, error } = await supabase.rpc('reports_branch_cash_closures_count', {
        p_branch_id: branchId,
        p_from: opts.fromIso,
        p_to: opts.toIso,
        p_opened_by: opts.openedBy ?? null,
        p_difference_filter: opts.differenceFilter ?? 'all',
      });
      if (error) {
        console.warn('[reportsRemote] reports_branch_cash_closures_count', mapRpcError(error));
        return null;
      }
      const n = Number(data);
      return Number.isFinite(n) ? n : null;
    },
    REPORTS_REMOTE_CACHE_MS,
    { category: 'reports', name: 'reports_branch_cash_closures_count' }
  );
}

export async function fetchRemoteBranchCashSummary(
  branchId: string,
  todayStartIso: string,
  options?: { force?: boolean }
): Promise<CashSummary | null> {
  if (options?.force) {
    return fetchRemoteBranchCashSummaryRemote(branchId, todayStartIso);
  }
  const cacheKey = reportsCacheKey(['cash_summary', branchId, todayStartIso]);
  return withReportsCache(
    cacheKey,
    () => fetchRemoteBranchCashSummaryRemote(branchId, todayStartIso),
    CASH_SUMMARY_REPORTS_CACHE_MS,
    { category: 'cash_summary', name: 'reports_branch_cash_summary' }
  );
}

async function fetchRemoteBranchCashSummaryRemote(
  branchId: string,
  todayStartIso: string
): Promise<CashSummary | null> {
  const { data, error } = await supabase.rpc('reports_branch_cash_summary', {
    p_branch_id: branchId,
    p_today_start: todayStartIso,
  });
  if (error) {
    console.warn('[reportsRemote] reports_branch_cash_summary', mapRpcError(error));
    return null;
  }
  const j = data as Record<string, unknown> | null;
  if (!j) return null;

  const active = parseCashSessionRow(j.active_session as Record<string, unknown> | null);
  const lastClosed = parseCashSessionRow(j.last_closed_session as Record<string, unknown> | null);

  const breakdownRaw = j.session_payment_breakdown;
  const sessionPaymentBreakdown = buildCashSessionPaymentBreakdown(
    Array.isArray(breakdownRaw)
      ? (breakdownRaw as { method_id?: string; total?: number }[])
      : []
  );

  return {
    activeSession: active,
    todaySalesCount: Number(j.today_sales_count ?? 0),
    todaySalesTotal: Number(j.today_sales_total ?? 0),
    todayCashTotal: Number(j.today_cash_total ?? 0),
    sessionCashTotal: Number(j.session_cash_total ?? 0),
    sessionSalesCount: Number(j.session_sales_count ?? 0),
    sessionSalesTotal: Number(j.session_sales_total ?? 0),
    sessionTipsTotal: Number(j.session_tips_total ?? 0),
    sessionPaymentBreakdown,
    sessionOutflowsTotal: Number(j.session_outflows_total ?? 0),
    sessionInflowsTotal: Number(j.session_inflows_total ?? 0),
    todayDigitalTotal: Number(j.today_digital_total ?? 0),
    lastClosedSession: lastClosed,
    expectedBalance:
      j.expected_balance != null && j.expected_balance !== ''
        ? Number(j.expected_balance)
        : null,
  };
}

export type CashActivityKind = 'cash_open' | 'cash_close' | 'outflow_create' | 'outflow_void';

export type CashActivityItem = {
  kind: CashActivityKind;
  id: string;
  sessionId: string;
  occurredAt: string;
  amount: number | null;
  category: string | null;
  note: string | null;
  actorUserId: string | null;
  status: string | null;
  voidReason: string | null;
  /** Solo eventos outflow_*: 'out' egreso (default) | 'in' ingreso manual. */
  direction: 'out' | 'in' | null;
};

function parseCashActivityItem(raw: Record<string, unknown>): CashActivityItem | null {
  const kind = String(raw.kind ?? '');
  if (
    kind !== 'cash_open' &&
    kind !== 'cash_close' &&
    kind !== 'outflow_create' &&
    kind !== 'outflow_void'
  ) {
    return null;
  }
  const occurredAt = String(raw.occurred_at ?? '');
  if (!occurredAt) return null;
  const amountRaw = raw.amount;
  const amount =
    amountRaw == null || amountRaw === ''
      ? null
      : Number.isFinite(Number(amountRaw))
        ? Number(amountRaw)
        : null;
  return {
    kind,
    id: String(raw.id ?? ''),
    sessionId: String(raw.session_id ?? ''),
    occurredAt,
    amount,
    category: raw.category != null ? String(raw.category) : null,
    note: raw.note != null ? String(raw.note) : null,
    actorUserId: raw.actor_user_id != null ? String(raw.actor_user_id) : null,
    status: raw.status != null ? String(raw.status) : null,
    voidReason: raw.void_reason != null ? String(raw.void_reason) : null,
    direction: raw.direction === 'in' ? 'in' : raw.direction === 'out' ? 'out' : null,
  };
}

export async function fetchRemoteCashActivity(
  branchId: string,
  opts?: { since?: string; limit?: number; offset?: number }
): Promise<{ items: CashActivityItem[]; limit: number; offset: number } | null> {
  return withReportsCache(
    reportsCacheKey([
      'reports_branch_cash_activity',
      branchId,
      opts?.since,
      opts?.limit ?? 40,
      opts?.offset ?? 0,
    ]),
    async () => {
      const { data, error } = await supabase.rpc('reports_branch_cash_activity', {
        p_branch_id: branchId,
        p_since: opts?.since ?? null,
        p_limit: opts?.limit ?? 40,
        p_offset: opts?.offset ?? 0,
      });
      if (error) {
        console.warn('[reportsRemote] reports_branch_cash_activity', mapRpcError(error));
        return null;
      }
      const j = data as Record<string, unknown> | null;
      if (!j) return null;
      const itemsRaw = j.items;
      const items: CashActivityItem[] = [];
      if (Array.isArray(itemsRaw)) {
        for (const row of itemsRaw) {
          if (row && typeof row === 'object') {
            const parsed = parseCashActivityItem(row as Record<string, unknown>);
            if (parsed) items.push(parsed);
          }
        }
      }
      return {
        items,
        limit: Number(j.limit ?? opts?.limit ?? 40),
        offset: Number(j.offset ?? opts?.offset ?? 0),
      };
    },
    REPORTS_REMOTE_CACHE_MS,
    { category: 'reports', name: 'reports_branch_cash_activity' }
  );
}

